"use client";

import Link from "next/link";
import { useMemo, useRef } from "react";
import { MapPin, Fuel, Users, Settings2, ShieldCheck, Zap, Crown } from "lucide-react";
import { useTranslations } from "next-intl";
import Badge from "@/components/ui/Badge";
import { useFormatPrice } from "@/components/price/useFormatPrice";
import type { Car } from "@/lib/types/database";
import { carDetailPath } from "@/lib/car-slug";
import { collectCarListingImageUrls } from "@/lib/car-identity";
import { carListingImageAlt } from "@/lib/car-image-alt";
import CarCardImageCarousel from "@/components/cars/CarCardImageCarousel";

export interface CarCardProps {
  car: Car;
  /** Первые карточки на главной — eager LCP */
  imagePriority?: boolean;
}

function normalizeListingTier(value: unknown): "standard" | "top" | "premium" {
  const t = String(value ?? "standard").trim().toLowerCase();
  if (t === "premium" || t === "top") return t;
  return "standard";
}

function catalogKey(prefix: "color" | "body", value: string | null | undefined): string | null {
  if (!value) return null;
  return `${prefix}.${value}`;
}

export default function CarCard({ car, imagePriority }: CarCardProps) {
  const t = useTranslations("CarCard");
  const tCat = useTranslations("Catalog");
  const tFilters = useTranslations("CarFilters");
  const formatMoney = useFormatPrice();
  const cardLinkRef = useRef<HTMLAnchorElement>(null);
  const tier = normalizeListingTier(car.listing_tier);
  const listingVerified = car.is_listing_verified === true;
  const detailHref = carDetailPath(car);
  const imageAlt = carListingImageAlt(car);

  const imageUrls = useMemo(() => collectCarListingImageUrls(car.images, 8), [car.images]);

  const colorKey = catalogKey("color", car.color);
  const bodyKey = catalogKey("body", car.body_type);
  const colorText = colorKey ? tCat(colorKey) : "";
  const bodyText = bodyKey ? tCat(bodyKey) : "";

  const engineVol = car.engine_volume;
  const engineText =
    engineVol === "electric"
      ? tFilters("engineElectric")
      : engineVol
        ? tFilters("engineLiters", { vol: engineVol })
        : "";

  const tierBorder =
    tier === "premium"
      ? "border-purple-200 ring-1 ring-purple-100 dark:border-purple-800 dark:ring-purple-900/40"
      : tier === "top"
        ? "border-amber-200 dark:border-amber-800"
        : "border-gray-100 dark:border-gray-800";

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-gray-900/80 ${tierBorder}`}
    >
      {/* Одна ссылка на карточку — под контентом; кнопки карусели вне <a> */}
      <Link
        ref={cardLinkRef}
        href={detailHref}
        prefetch
        className="absolute inset-0 z-[1] rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-[#E63946]/35 focus-visible:ring-offset-2 dark:focus-visible:ring-[#E63946]/40"
        aria-label={car.title}
      />

      <div className="relative z-[2] flex flex-1 flex-col pointer-events-none">
        <div className="relative h-48 shrink-0 bg-gray-100 pointer-events-auto dark:bg-gray-800">
          <CarCardImageCarousel
            urls={imageUrls}
            alt={imageAlt}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 280px"
            imagePriority={imagePriority}
            onImageTap={() => cardLinkRef.current?.click()}
          />
          {!car.is_available && (
            <div className="pointer-events-none absolute inset-0 z-[11] flex items-center justify-center bg-black/50">
              <span className="text-sm font-medium text-white">{t("unavailable")}</span>
            </div>
          )}
          <div className="pointer-events-none absolute left-3 top-3 z-[11] flex flex-col gap-1.5">
            {tier === "premium" && (
              <div className="flex w-fit items-center gap-1 rounded-full bg-purple-600 px-2 py-1 text-[11px] font-bold text-white shadow">
                <Crown size={10} />
                {t("premiumBadge")}
              </div>
            )}
            {tier === "top" && (
              <div className="flex w-fit items-center gap-1 rounded-full bg-amber-500 px-2 py-1 text-[11px] font-bold text-white shadow">
                <Zap size={10} />
                {t("topBadge")}
              </div>
            )}
            {listingVerified && (
              <Badge variant="success" className="w-fit gap-1 shadow-sm">
                <ShieldCheck size={11} />
                {t("verified")}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-1 flex-col p-4">
          <div className="mb-2 flex items-start justify-between gap-2">
            <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-100">
              {car.title}
            </h3>
            <span className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{car.year}</span>
          </div>

          <div className="mb-3 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Users size={12} />
              {t("seats", { count: car.seats })}
            </span>
            <span className="flex items-center gap-1">
              <Settings2 size={12} />
              {tCat(`transmission.${car.transmission}` as "transmission.manual" | "transmission.automatic")}
            </span>
            <span className="flex items-center gap-1">
              <Fuel size={12} />
              {tCat(
                `fuel.${car.fuel_type}` as "fuel.petrol" | "fuel.diesel" | "fuel.electric" | "fuel.hybrid",
              )}
            </span>
            {car.body_type && <span className="text-gray-500">{bodyText}</span>}
            {car.color && <span className="text-gray-500">{colorText}</span>}
            {car.engine_volume && <span className="text-gray-500">{engineText}</span>}
          </div>

          {car.location && (
            <p className="mb-3 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
              <MapPin size={11} />
              {car.location}
            </p>
          )}

          <div className="mt-auto flex items-end justify-between">
            <div>
              <span className="text-xl font-bold leading-snug text-gray-900 dark:text-gray-100">
                {formatMoney(car.price_per_day)}
              </span>
              <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">{t("perDay")}</span>
            </div>
            <span className="text-xs font-medium text-[#E63946]">{t("details")}</span>
          </div>
        </div>
      </div>
    </article>
  );
}
